import { useContext, useEffect, useState } from 'react';
import { AssignmentTurnedIn } from '@mui/icons-material';
import { Alert, Box, Chip, Fab, Grid, Typography } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { useNavigate, useParams } from 'react-router-dom';
import { decryptURL } from 'utils/functions';
import { toastWarning } from 'utils/toast';
import { DocumentosContext } from 'contexts/DocumentosContext';
import { useDialog } from 'components/Modals';
import Container from 'components/Container';
import Loader from 'components/Loader';
import Header from 'components/Header';
import Button from 'components/Button';
import localeText from 'utils/localeText';
import RespostasModal from './RespostasModal';
import styles from './styles';

const Qualidade = () => {
  const navigate = useNavigate();
  const { hash } = useParams();
  const { openDialog } = useDialog();
  const { getLoading, getQuestionario, postLoading, postQuestionario } =
    useContext(DocumentosContext);
  const [questionario, setQuestionario] = useState([]);
  const params = Boolean(hash) ? decryptURL(hash) : {};

  const loadData = () => getQuestionario({ params, cb: setQuestionario });

  const onCellEditCommit = (params, documentoItemQualidadeId) => {
    const { id, field, value } = params;

    setQuestionario((prev) =>
      prev.map((pergunta) => {
        if (pergunta.id !== documentoItemQualidadeId) {
          return pergunta;
        }

        return {
          ...pergunta,
          Pergunta: {
            ...pergunta.Pergunta,
            Opcoes: pergunta.Pergunta.Opcoes.map((opcao) =>
              opcao.id === id
                ? {
                    ...opcao,
                    [field]: value === '' ? null : value,
                  }
                : opcao
            ),
          },
        };
      })
    );
  };

  const onSubmit = () => {
    const isRespondida = (opcao) =>
      opcao.resultado !== null && opcao.resultado !== undefined;

    const questionariosRespondidos = questionario.filter((item) =>
      item.Pergunta.Opcoes.some(isRespondida)
    );

    if (!questionariosRespondidos.length) {
      return toastWarning(
        'Informe os resultados de pelo menos um questionário antes de enviar.'
      );
    }

    const hasQuestionarioIncompleto = questionariosRespondidos.some((item) =>
      item.Pergunta.Opcoes.some((opcao) => !isRespondida(opcao))
    );

    if (hasQuestionarioIncompleto) {
      return toastWarning(
        'Preencha todos os resultados dos questionários iniciados antes de enviar.'
      );
    }

    const data = questionariosRespondidos.flatMap((item) =>
      item.Pergunta.Opcoes.map((opcao) => ({
        documento_item_qualidade_id: item.id,
        qualidade_indicador_resp_id: opcao.id,
        quantidade: opcao.quantidade ?? null,
        resultado: opcao.resultado ?? null,
        conforme: opcao.conforme ?? null,
        observacao: opcao.observacao ?? null,
      }))
    );

    postQuestionario({ data, cb: () => navigate(-1) });
  };

  useEffect(() => {
    loadData();
  }, []);

  if (getLoading) {
    return <Loader />;
  }

  return (
    <Container>
      <Header titulo="Questionário de Qualidade" />
      <Grid container spacing={10}>
        {!questionario.length && (
          <Grid item xs={12} textAlign="center">
            <Typography>Nenhuma pergunta encontrada.</Typography>
          </Grid>
        )}
        {questionario.map((item) => (
          <Grid item xs={12} key={item?.id}>
            <Box display="flex" flexDirection="column" gap={2}>
              <Box>
                <Box display="flex" alignItems="center" gap={2}>
                  <Typography variant="h6" fontWeight="bold">
                    #{item?.sequencia}
                  </Typography>
                  <Chip
                    size="small"
                    variant="outlined"
                    label={item?.Pergunta?.grupo}
                  />
                  <Typography variant="h6" fontWeight="bold" flex={1}>
                    {item?.Pergunta?.descricao}
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {item?.Pergunta?.orientacao}
                </Typography>
              </Box>
              <Alert severity="info">
                <Typography variant="body2">
                  <strong>Quantidade:</strong> número de itens do produto
                  analisados para chegar ao resultado.
                </Typography>
                <Typography variant="body2">
                  <strong>Resultado:</strong> valor medido e informado no
                  questionário de qualidade.
                </Typography>
              </Alert>
              <DataGrid
                rows={item?.Pergunta?.Opcoes || []}
                columns={[
                  {
                    field: 'descricao',
                    headerName: 'Descrição',
                    sortable: false,
                    editable: false,
                    minWidth: 300,
                    flex: 1,
                  },
                  {
                    field: 'quantidade',
                    headerName: 'Quantidade',
                    type: 'number',
                    sortable: false,
                    editable: true,
                    width: 150,
                  },
                  {
                    field: 'resultado',
                    headerName: 'Resultado',
                    type: 'number',
                    sortable: false,
                    editable: true,
                    width: 150,
                    valueParser: (value) => {
                      if (
                        value === '' ||
                        value === null ||
                        value === undefined
                      ) {
                        return null;
                      }

                      const number = Number(value);
                      return Number.isNaN(number) ? null : number;
                    },
                    renderCell: ({ row }) => (
                      <Box
                        flex={1}
                        display="flex"
                        alignItems="end"
                        flexDirection="column"
                      >
                        <Typography>{row?.resultado ?? ''}</Typography>
                        {(row?.minimo != null || row?.maximo != null) && (
                          <Typography variant="caption" color="text.secondary">
                            {row?.minimo != null && `Mín: ${row.minimo}`}
                            {row?.minimo != null &&
                              row?.maximo != null &&
                              ' • '}
                            {row?.maximo != null && `Máx: ${row.maximo}`}
                          </Typography>
                        )}
                      </Box>
                    ),
                    preProcessEditCellProps: (params) => {
                      const rawValue = params.props.value;

                      if (
                        rawValue === '' ||
                        rawValue === null ||
                        rawValue === undefined
                      ) {
                        return {
                          ...params.props,
                          value: null,
                          error: false,
                        };
                      }

                      const value = Number(rawValue);
                      const { minimo, maximo } = params.row;

                      const hasError =
                        Number.isNaN(value) ||
                        (minimo != null && value < Number(minimo)) ||
                        (maximo != null && value > Number(maximo));

                      return {
                        ...params.props,
                        error: false,
                      };
                    },
                  },
                  // {
                  //   field: 'conforme',
                  //   headerName: 'Conformidade',
                  //   type: 'singleSelect',
                  //   sortable: false,
                  //   editable: true,
                  //   width: 300,
                  //   valueOptions: [
                  //     {
                  //       value: null,
                  //       label: 'Nenhuma opção',
                  //     },
                  //     {
                  //       value: 'CONFORME',
                  //       label: 'Conforme',
                  //     },
                  //     {
                  //       value: 'NAO CONFORME COM POSSIBILIDADE DE RETOQUE',
                  //       label: 'Não conforme com possibilidade de retoque',
                  //     },
                  //     {
                  //       value: 'NAO CONFORME PARA SER DESTRUIDO',
                  //       label: 'Não conforme para ser destruído',
                  //     },
                  //   ],
                  // },
                  {
                    field: 'observacao',
                    headerName: 'Observação',
                    sortable: false,
                    editable: true,
                    width: 300,
                  },
                ]}
                rowHeight={72}
                autoHeight
                hideFooter
                density="compact"
                disableSelectionOnClick
                disableColumnMenu
                showCellRightBorder
                showColumnRightBorder
                localeText={localeText}
                onCellEditCommit={(params) => onCellEditCommit(params, item.id)}
              />
            </Box>
          </Grid>
        ))}
        <Grid item xs={12} textAlign="center" mb={2}>
          <Button variant="contained" onClick={onSubmit} loading={postLoading}>
            Salvar
          </Button>
        </Grid>
      </Grid>
      <Fab
        color="primary"
        sx={styles?.fab}
        onClick={() =>
          openDialog(<RespostasModal params={params} />, 'Respostas')
        }
      >
        <AssignmentTurnedIn />
      </Fab>
    </Container>
  );
};

export default Qualidade;
